import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.string.trim.js";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useDebouncedCallback } from 'use-debounce';
import { useTheme } from 'styled-components';
import { useCreateMyFeedback, useUpdateMyFeedback } from 'actions/feedback';
import { usersQueryKeys } from 'actions/users';
import { getUniqueId } from 'hooks/useUniqueId';
import { getDimmedTextColor } from 'utils/ui';
import getUserId from 'utils/session/getUserId';
import { typeScale } from 'styles/globals';
import Flex, { Box } from 'components/Flex';
import Button from 'components/Button';
import Text from 'components/Text';
import { Like as LikeIcon } from 'components/icons';
import TextArea from 'components/TextArea';
function Feedback(_ref) {
  var userMeetingSkillId = _ref.userMeetingSkillId,
    meetingId = _ref.meetingId,
    userId = _ref.userId,
    feedback = _ref.feedback,
    skillName = _ref.skillName;
  var _useState = useState(feedback === null || feedback === void 0 ? void 0 : feedback.id),
    _useState2 = _slicedToArray(_useState, 2),
    feedbackId = _useState2[0],
    setFeedbackId = _useState2[1];
  var _useState3 = useState(feedback === null || feedback === void 0 ? void 0 : feedback.rating),
    _useState4 = _slicedToArray(_useState3, 2),
    rating = _useState4[0],
    setRating = _useState4[1];
  var _useState5 = useState((feedback === null || feedback === void 0 ? void 0 : feedback.comment) || ''),
    _useState6 = _slicedToArray(_useState5, 2),
    comment = _useState6[0],
    setComment = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    showCommentBox = _useState8[0],
    setShowCommentBox = _useState8[1];
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var theme = useTheme();
  var queryClient = useQueryClient();
  var userIdForFeedback = userId || getUserId();
  function onFeedbackSuccess() {
    queryClient.invalidateQueries(usersQueryKeys.singleUserMeetingAnalysis(meetingId, userIdForFeedback));
  }
  var _useCreateMyFeedback = useCreateMyFeedback({
      refId: userMeetingSkillId,
      onSuccess: function onSuccess() {
        return onFeedbackSuccess();
      }
    }),
    createMyFeedback = _useCreateMyFeedback.createMyFeedback;
  var _useUpdateMyFeedback = useUpdateMyFeedback({
      onSuccess: function onSuccess() {
        return onFeedbackSuccess();
      }
    }),
    updateMyFeedback = _useUpdateMyFeedback.updateMyFeedback,
    isUpdateLoading = _useUpdateMyFeedback.isLoading;
  var handleSubmitRating = useCallback(/*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(rating) {
      var _yield$createMyFeedba, id;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (!(!feedbackId && rating === null)) {
              _context.next = 4;
              break;
            }
            return _context.abrupt("return");
          case 4:
            if (!(!feedbackId && rating !== null)) {
              _context.next = 12;
              break;
            }
            _context.next = 7;
            return createMyFeedback({
              type: 'helix_user_meeting_skill',
              rating: rating
            });
          case 7:
            _yield$createMyFeedba = _context.sent;
            id = _yield$createMyFeedba.id;
            setFeedbackId(id);
            _context.next = 15;
            break;
          case 12:
            if (!feedbackId) {
              _context.next = 15;
              break;
            }
            _context.next = 15;
            return updateMyFeedback({
              rating: rating,
              id: feedbackId
            });
          case 15:
            setRating(rating);
            if (rating === null) {
              setShowCommentBox(false);
            } else {
              setShowCommentBox(true);
            }
          case 17:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [feedbackId, updateMyFeedback, createMyFeedback]);
  var _useDebouncedCallback = useDebouncedCallback(handleSubmitRating, 200),
    _useDebouncedCallback2 = _slicedToArray(_useDebouncedCallback, 1),
    submitFeedbackRatingDebounced = _useDebouncedCallback2[0];
  var hasRating = rating !== null && rating !== undefined;
  var isPositiveRating = rating === true;
  var isNegativeRating = rating === false;
  var neutralColour = getDimmedTextColor(theme);
  var positiveColour = isPositiveRating ? 'positive' : neutralColour;
  var negativeColour = isNegativeRating ? 'negative' : neutralColour;
  var placeholderText = t(isPositiveRating ? 'skills_feedback_placeholder_rating_positive' : 'skills_feedback_placeholder_rating_negative');
  var ctaTextKey = hasRating ? showCommentBox ? isPositiveRating ? 'skills_feedback_what_was_helpful' : 'skills_feedback_what_was_unhelpful' : 'skills_feedback_thanks' : 'skills_feedback_was_analysis_helpful';
  var ctaTextId = getUniqueId();
  var showNegativeButton = showCommentBox || !hasRating || isNegativeRating;
  var showPositiveButton = showCommentBox || !hasRating || isPositiveRating;
  var showEditButton = hasRating && !showCommentBox;
  return /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "right",
    alignItems: "right",
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Flex, {
    gap: "xxxs",
    justifySelf: "right",
    mt: "xs",
    mr: "m"
  }, /*#__PURE__*/React.createElement(Text, {
    dimmed: true,
    size: "xs",
    px: "xxs",
    id: ctaTextId
  }, t(ctaTextKey)), showPositiveButton && /*#__PURE__*/React.createElement(Button, {
    isActive: isPositiveRating,
    iconOnly: true,
    icon: /*#__PURE__*/React.createElement(LikeIcon, {
      color: positiveColour
    }),
    size: "small",
    variant: "transparent",
    onClick: function onClick() {
      return submitFeedbackRatingDebounced(rating === true ? null : true);
    }
  }, t('skills_feedback_analysis_helpful', {
    name: skillName
  })), showNegativeButton && /*#__PURE__*/React.createElement(Button, {
    isActive: isNegativeRating,
    iconOnly: true,
    icon: /*#__PURE__*/React.createElement(LikeIcon, {
      rotate: 180,
      flip: "x",
      color: negativeColour
    }),
    size: "small",
    variant: "transparent",
    onClick: function onClick() {
      return submitFeedbackRatingDebounced(rating === false ? null : false);
    }
  }, t('skills_feedback_analysis_not_helpful', {
    name: skillName
  })), showEditButton && /*#__PURE__*/React.createElement(Button, {
    icon: "edit",
    size: "small",
    variant: "transparent",
    onClick: function onClick() {
      setShowCommentBox(true);
    }
  }, t('actions_edit'))), showCommentBox && feedbackId && /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    gap: "s",
    mt: "xs",
    align: "right"
  }, /*#__PURE__*/React.createElement(Box, {
    breakpoints: "xxs",
    minWidth: ['250px', '500px'],
    maxWidth: "500px"
  }, /*#__PURE__*/React.createElement(TextArea
  // eslint-disable-next-line jsx-a11y/no-autofocus
  , {
    autoFocus: true,
    style: {
      fontSize: typeScale.s
    },
    minRows: 4,
    "data-autofocus": true,
    placeholder: placeholderText,
    value: comment,
    onChange: function onChange(e) {
      return setComment(e.target.value);
    },
    "aria-labelledby": ctaTextId
  })), /*#__PURE__*/React.createElement(Button, {
    mr: "m",
    isDisabled: isUpdateLoading,
    variant: "primary",
    size: "small",
    onClick: /*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return updateMyFeedback({
              comment: comment.trim(),
              id: feedbackId
            });
          case 2:
            setShowCommentBox(false);
          case 3:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }))
  }, t('actions_submit_feedback'))));
}
export default Feedback;